import React, { useState, useEffect, useCallback, memo } from 'react';
import {
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormHelperText,
  Box,
} from '@mui/material';
import { useAppSelector } from 'src/store';

interface Options {
  label: string;
  value: string | number | boolean;
}

interface RadioButtonGroupProps {
  label?: string;
  name: string;
  options: Options[] | null;
  mode?: 'yeno' | 'yenona' | 'yenouk';
  writeOnly?: boolean;
  viewOnly?: boolean;
  required?: boolean;
  error?: string;
  value?: string | number | boolean;
  onChange?: (value: boolean | number) => void;
}

const RadioButtonGroup: React.FC<RadioButtonGroupProps> = ({
  label,
  name,
  options,
  mode,
  writeOnly = false,
  viewOnly = false,
  required = false,
  error,
  value,
  onChange,
}) => {
  const [isViewMode, setIsViewMode] = useState(true);
  const [populatedOptions, setPopulatedOptions] = useState<Options[]>(
    options || [],
  );
  const reduxFormMode = useAppSelector((state) => state.formPermissions.mode);

  const setFormPermissionValues = useCallback(() => {
    if (reduxFormMode) {
      if (writeOnly) {
        setIsViewMode(false);
      } else {
        setIsViewMode(viewOnly || reduxFormMode === 'view');
      }
    }
  }, [reduxFormMode, writeOnly, viewOnly]);

  useEffect(() => {
    setFormPermissionValues();
  }, [setFormPermissionValues]);

  // Form elements are always converted to string in the DOM so we have to
  // convert the value to boolean before passing it to the RadioGroup component.
  useEffect(() => {
    if (mode) {
      switch (mode) {
        case 'yeno': {
          setPopulatedOptions([
            { label: 'Yes', value: true },
            { label: 'No', value: false },
          ]);
          break;
        }
        case 'yenona': {
          setPopulatedOptions([
            { label: 'Yes', value: 1 },
            { label: 'No', value: 0 },
            { label: 'N/A', value: 2 },
          ]);
          break;
        }
        case 'yenouk': {
          setPopulatedOptions([
            { label: 'Yes', value: 1 },
            { label: 'No', value: 0 },
            { label: 'Unknown', value: 2 },
          ]);
          break;
        }
      }
    } else {
      setPopulatedOptions(options || []); // Fallback to an empty array if options is null. Otherwise it will throw an error in the UI.
    }
  }, [mode, options]);

  // HTML form elements always return their values as strings when an event is triggered. Even tho we set the type in populatedOptions, the browser converts these values to strings when the user interacts with the form, which we fix here.
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue =
      event.target.value === 'true'
        ? true
        : event.target.value === 'false'
          ? false
          : !isNaN(Number(event.target.value))
            ? Number(event.target.value)
            : event.target.value;
    if (
      onChange &&
      (typeof newValue === 'number' || typeof newValue === 'boolean')
    ) {
      onChange(newValue);
    }
  };

  // Note: We handle mobileView rendering in the useMediaQuery hook for the other form elements here but for the current structure of the file that consumes this radioButton, we fallback on a different component for mobile view rendering. Update this in a future PR to add a mobileView and move away from conditional component rendering.
  return (
    <Box>
      <FormControl
        component="fieldset"
        disabled={isViewMode}
        error={!!error}
      >
        {label && (
          <FormLabel component="legend">
            {label} {required && <span style={{ color: 'red' }}>*</span>}
          </FormLabel>
        )}
        <RadioGroup
          name={name}
          value={value}
          onChange={handleChange}
          row
          sx={{
            display: 'flex',
            flexWrap: 'nowrap',
            '.MuiFormControlLabel-root': {
              marginRight: '8px',
            },
            '.MuiRadio-root': {
              transform: 'scale(0.9)',
            },
            cursor: isViewMode ? 'not-allowed' : 'pointer',
          }}
        >
          {populatedOptions.map((option) => (
            <FormControlLabel
              key={String(option.value)}
              value={option.value}
              control={
                <Radio sx={{ transform: 'scale(0.7)', marginLeft: '0px' }} />
              }
              label={option.label}
              disabled={isViewMode}
              sx={{
                '.MuiFormControlLabel-label': {
                  fontSize: '0.75rem',
                },
                marginLeft: '0px',
              }}
            />
          ))}
        </RadioGroup>
        {error && !isViewMode && (
          <FormHelperText
            sx={{
              whiteSpace: 'nowrap',
              color: 'red',
              margin: 0, // aligns the error with the radio buttons and prevent padding being added to the right of the error message.
            }}
          >
            {error}
          </FormHelperText>
        )}
      </FormControl>
    </Box>
  );
};

export default memo(RadioButtonGroup);
